import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api } from "@/lib/api";
import { BOOK } from "@/constants/testIds";

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [related, setRelated] = useState([]);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, -80]);

  useEffect(() => {
    api.get(`/books/${slug}`).then((r) => {
      setBook(r.data);
      api.get("/books").then((res) => {
        setRelated(res.data.filter((b) => b.slug !== slug).slice(0, 3));
      });
    });
  }, [slug]);

  if (!book) {
    return (
      <main className="pt-40 px-6 md:px-10 min-h-screen">
        <div className="text-[color:var(--vv-bone-dim)] text-sm">Loading…</div>
      </main>
    );
  }

  return (
    <main className="pt-24 md:pt-32 pb-32">
      <section className="px-6 md:px-10">
        <div className="max-w-[1600px] mx-auto">
          <Link
            to="/catalog"
            className="text-xs uppercase tracking-[0.3em] text-[color:var(--vv-bone-dim)] vv-link"
          >
            ← Catalog
          </Link>

          <div className="mt-8 grid grid-cols-12 gap-8 md:gap-16">
            <motion.div
              style={{ y }}
              className="col-span-12 md:col-span-5 md:sticky md:top-28 self-start"
            >
              <div className="book-frame relative border vv-line p-8 md:p-14 bg-[color:var(--vv-surface)]">
                <motion.img
                  data-testid={BOOK.cover}
                  src={book.cover_url}
                  alt={book.title}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.4, ease: [0.19, 1, 0.22, 1] }}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
              <div className="mt-4 flex justify-between text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-bone-dim)]">
                <span>Cover · A Lee Creations</span>
                <span>{book.year}</span>
              </div>
            </motion.div>

            <div className="col-span-12 md:col-span-7">
              <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-ember)]">
                {book.genre} · N°{" "}
                {String(book.year).slice(-2)}
                {String(book.id).slice(-3)}
              </div>
              <h1
                data-testid={BOOK.title}
                className="mt-6 font-serif-vv text-6xl md:text-8xl leading-[0.9] tracking-tight"
              >
                {book.title}
              </h1>
              {book.subtitle && (
                <p className="mt-4 font-serif-vv italic text-2xl text-[color:var(--vv-bone-dim)]">
                  {book.subtitle}
                </p>
              )}
              <div className="mt-8 text-lg">
                by{" "}
                <Link
                  to={`/authors`}
                  className="font-serif-vv italic vv-link text-[color:var(--vv-bone)]"
                >
                  {book.author_name}
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 border-y vv-line py-6 text-[10px] tracking-[0.3em] uppercase text-[color:var(--vv-bone-dim)]">
                <div>
                  <div className="opacity-60">Year</div>
                  <div className="text-[color:var(--vv-bone)] mt-2 text-sm">
                    {book.year}
                  </div>
                </div>
                <div>
                  <div className="opacity-60">Pages</div>
                  <div className="text-[color:var(--vv-bone)] mt-2 text-sm">
                    {book.pages || "—"}
                  </div>
                </div>
                <div>
                  <div className="opacity-60">Genre</div>
                  <div className="text-[color:var(--vv-bone)] mt-2 text-sm">
                    {book.genre || "—"}
                  </div>
                </div>
              </div>

              <p
                data-testid={BOOK.blurb}
                className="mt-12 font-serif-vv text-2xl md:text-3xl leading-snug text-[color:var(--vv-bone)]/90 whitespace-pre-line"
              >
                {book.blurb}
              </p>

              <div className="mt-16 flex flex-wrap gap-4">
                {book.coming_soon ? (
                  <div className="w-full">
                    <div className="inline-flex items-center gap-3 border border-[color:var(--vv-ember)] px-6 py-3 text-xs uppercase tracking-[0.3em] text-[color:var(--vv-ember)]">
                      ✦ Coming Soon
                    </div>
                    <p className="mt-4 text-sm text-[color:var(--vv-bone-dim)] max-w-md">
                      Pre-order links coming soon. Join the correspondence list
                      below to receive first word when this title ships.
                    </p>
                  </div>
                ) : (
                  <>
                    {book.amazon_url && (
                      <a
                        href={book.amazon_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={BOOK.buyAmazon}
                        className="group inline-flex items-center gap-3 border vv-line px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-[color:var(--vv-bone)] hover:text-[color:var(--vv-bg)] transition-colors duration-500"
                      >
                        Buy on Amazon
                        <span className="group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </a>
                    )}
                    {book.bookshop_url && (
                      <a
                        href={book.bookshop_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={BOOK.buyBookshop}
                        className="group inline-flex items-center gap-3 border vv-line px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-[color:var(--vv-ember)] hover:border-[color:var(--vv-ember)] hover:text-[color:var(--vv-bg)] transition-colors duration-500"
                      >
                        Buy at Bookshop.org
                        <span className="group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mt-40 px-6 md:px-10 border-t vv-line pt-24">
          <div className="max-w-[1600px] mx-auto">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[color:var(--vv-bone-dim)] mb-10">
              — Also from the Vault
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
              {related.map((b) => (
                <Link key={b.id} to={`/book/${b.slug}`} className="group">
                  <div className="book-frame border vv-line p-8 aspect-[3/4] bg-[color:var(--vv-surface)]">
                    <img
                      src={b.cover_url}
                      alt={b.title}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                    />
                  </div>
                  <h3 className="mt-4 font-serif-vv text-2xl">{b.title}</h3>
                  <div className="text-sm italic text-[color:var(--vv-bone-dim)]">
                    {b.author_name}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
